import { Typography, useMediaQuery } from "@mui/material";
import {React, useEffect, useState, useRef} from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import CommonTextField from "../../component/Input";
import CommonButton from "../../component/Button";
import { CommonGrid2 } from "../../modules/common-components/StyledComponents";
import { CustomDiv } from "../chat-bot/style";
import { platformBlackLogo } from "../../utils/logo";
import {ReactComponent as DynamicIntelligenceHaileyIco} from "../../assets/images/dynamic-intelligence-hailey.svg";
import { GetDynamicIntelligenceDetails, DynamicIntelligenceChat } from "../../redux/action/DynamicIntelligence";
import ChatView from "./ChatView";
import SnackBar from "../../component/SnackBar";
import { Fragment } from "react";
import ExpiryScreen from "./ExpiryScreen";

function DynamicIntelligence() {
    const [details, setDetails] = useState({
        email: "", 
        error: "", 
        secret_key:"",
        job_info:{
            job_title:"",
            company_name:"",
            req_id:""
        },
        name:"",
        suggestions:[
            {question:"Latest updates in this job", icon_type:"chat"},
            {question: "Ask about Candidates", icon_type:"candidate"},
            {question: "Ask about Job", icon_type:"job"}
        ],
        chat_history:[],
        org_logo_url:null,
        org_label:null,
    });
    const param = useParams();
    const unique_id = param.id;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const emailParam = searchParams.get("email");
    const expired = searchParams.get("expired");
    const [loading, setLoading] = useState(false);
    const [snackbarState, setSnackbarState] = useState({
        open: false,
        message: "",
        severity: "",
    });
    const location = useLocation();

    const handleVerify = async() => {
        if (!details.email)
            setDetails((prev) => ({...prev, error: "Email is required"}));
        else{
            const data = await getDetails(details.email);
            if(data.secret_key){
            navigate(`/dynamic-intelligence/${unique_id}/?email=${details.email}`)
        }}
    }

    const handleCloseValidation = () => {
        setSnackbarState((prev) => ({...prev, open: false}));
    }

    const getDetails = async(emailParam) => {
        try{
            setLoading(true);
            const response = await GetDynamicIntelligenceDetails(unique_id,emailParam);
            setLoading(false);
            setDetails((prev) => ({
                ...prev, 
                secret_key: response.secret_key,
                job_info: response.job_info || prev.job_info,
                name: response.name || prev.name,
                suggestions: response.suggestions || prev.suggestions,
                email: emailParam,
                chat_history: response.chat_history || prev.chat_history,
                org_label: response.org_label || prev.org_label,
                org_logo_url: response.org_logo_url || prev.org_logo_url,
            }));
            return response;
        } catch (error){
            setLoading(false);
            console.log(error,"error");
            if(error.status === 400){
                navigate(`/dynamic-intelligence/${unique_id}/?expired=true`)
            }
            setSnackbarState({
                open: true, 
                message: error.message, 
                severity: "error"
            });
            return error;
        }
    }

    useEffect(() => {
        if (emailParam)
            getDetails(emailParam);
        else if (expired)
            navigate(`/dynamic-intelligence/${unique_id}`)
    },[])

    // ChatView state and handlers
    const lg = useMediaQuery("(min-width:767px)");
    const [isBottomVisible, setIsBottomVisible] = useState(true);
    const [showSuggestions, setShowSuggestions] = useState(true);
    const [chatBotData, setChatBotData] = useState({
        message: [],
        value: "",
        loader: false,
        pageLoader: false,
        details: "See Details",
    });
    const customDivRef = useRef(null);
    const [customDivHeight, setCustomDivHeight] = useState(0);

    // Transform chat history to message format
    useEffect(() => {
        if (details?.chat_history && Array.isArray(details.chat_history)) {
            const transformedMessages = [];
            details.chat_history.forEach((item) => {
                if (item.user) {
                    transformedMessages.push({
                        sender: "user",
                        text: item.user
                    });
                }
                if (item.assistant) {
                    transformedMessages.push({
                        sender: "bot",
                        text: item.assistant
                    });
                }
            });
            setChatBotData((prev) => ({
                ...prev,
                message: transformedMessages,
            }));
        }
    }, [details?.chat_history]);

    // Measure custom div height
    useEffect(() => {
        if (!customDivRef.current) return;
      
        const observer = new ResizeObserver(entries => {
          const rect = entries[0].contentRect;
          setCustomDivHeight(rect.height);
        });
      
        observer.observe(customDivRef.current);
      
        return () => observer.disconnect();
    }, [location]);

    // Chat handlers
    const handleChatBotChange = (event) => {
        setChatBotData((prev) => ({
          ...prev,
          value: event.target.value,
        }));
        if (event.key === "Enter") {
          handleSendMessage();
        }
    };

    const handleButtonClickByLabel = async (item) => {
        const senderResponse = {
            sender: "user",
            text: item.question,
        };
        setChatBotData((prev) => ({
            ...prev,
            message: [...prev.message, senderResponse],
            loader: true,
        }));

        let payload = {
            query: item.prompt || item.question,
            user_email: details?.email,
            secret_key: details?.secret_key
        };

        try {
            const data = await DynamicIntelligenceChat(payload);
            const botResponse = {
                sender: "bot",
                text: data?.data?.response,
            };
            setChatBotData((prev) => ({
                ...prev,
                message: [...prev.message, botResponse],
                loader: false,
            }));
        } catch (error) {
            const botResponse = {
                sender: "error",
                text: error?.data?.message || error?.message || "An error occurred",
            };
            setChatBotData((prev) => ({
                ...prev,
                message: [...prev.message, botResponse],
                loader: false,
            }));
        }
    };

    const handleSendMessage = async () => {
        if (!chatBotData.value.trim()) return;
        let payload = {
          query: chatBotData.value,
          user_email: details?.email,
          secret_key: details?.secret_key
        };
    
        setChatBotData((prev) => ({
          ...prev,
          message: [...prev.message, { sender: "user", text: chatBotData.value }],
          loader: true,
          value: "",
        }));
    
        try {
          const data = await DynamicIntelligenceChat(payload);
          const botResponse = {
            sender: "bot",
            text: data?.data?.response,
          };
          setChatBotData((prev) => ({
            ...prev,
            message: [...prev.message, botResponse],
            loader: false,
          }));
        } catch (error) {
          const botResponse = {
            sender: "error",
            text: error?.data?.message || error?.message || "An error occurred",
          };
          setChatBotData((prev) => ({
            ...prev,
            message: [...prev.message, botResponse],
            loader: false,
            value: "",
          }));
        }
    };

    return (

    <Fragment>
    { expired ? 
        <ExpiryScreen 
            details={details} 
            setDetails={setDetails} 
            loading={loading} 
            handleVerify={handleVerify} 
            lg={lg}
        /> : emailParam ? (
            <ChatView 
                details={details}
                setDetails={setDetails}
                lg={lg}
                isBottomVisible={isBottomVisible}
                setIsBottomVisible={setIsBottomVisible}
                showSuggestions={showSuggestions}
                setShowSuggestions={setShowSuggestions}
                chatBotData={chatBotData}
                setChatBotData={setChatBotData}
                customDivRef={customDivRef}
                customDivHeight={customDivHeight}
                handleChatBotChange={handleChatBotChange}
                handleButtonClickByLabel={handleButtonClickByLabel}
                handleSendMessage={handleSendMessage}
                skeletonLoader={loading}
                setSkeletonLoader={setLoading}
            />
    ) : (
        <CustomDiv background={"#EBE1F9"}>
        <CommonGrid2
            display="flex"
            flexDirection="column"
            gap="1rem"
            sx={{
                width: {
                  xs: "90%",
                  sm: "80%",   
                  md: "400px",  
                }
              }}
            alignItems={"center"}
            justifyContent={"center"}
            height={"100vh"}
            customStyle={{ margin: "0 auto" }}
        >
            <CommonGrid2
                background='#fff' 
                height={"max-content"} 
                width={"100%"}
                borderRadius={"12px"}
                display={"flex"}
                gap={"1rem"}
                flexDirection={"row"}
            >
                <CustomDiv
                    height={"100%"}
                    padding={"10px 10px 0px 16px"}
                    display={"flex"}
                >
                    <DynamicIntelligenceHaileyIco />
                </CustomDiv>
                <CommonGrid2
                    display={"flex"}
                    flexDirection={"column"}
                    justifyContent={"center"}
                    alignItems={"start"}
                >
                    <Typography 
                        variant={lg ? "h3" : "h3xs"} 
                        fontWeight={"700"}
                        sx={{
                            background: "linear-gradient(98.47deg, #5A35B2 1.87%, #FA4F96 54.6%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                        }}
                    >Hello, {details.name}</Typography>
                    <Typography 
                        variant={lg ? "h5" : "h5xs"}
                        color={"#5B738B"}
                        fontWeight={"400"}
                    >
                        I am Hailey. I'm here to help you understand the job.
                    </Typography>
                </CommonGrid2>
            </CommonGrid2>
            <CustomDiv 
                background='#fff' 
                height={"max-content"} 
                width={"100%"}
                padding={"1rem"} 
                borderRadius={"12px"}
                display={"flex"}
                gap={lg ? "1rem" : "0.5rem"}
                customStyle={{ flexDirection: "column" }}
            >
            <Typography 
                variant={lg ? "body1" : "body1xs"}
                color={"#5B738B"}
                textAlign={"left"}
                width={"100%"}
                fontWeight={"400"}
            >Please verify your Email Address</Typography>
            <CommonTextField
                label={"Enter Email Address"}
                placeholder={"john.doe@example.com"}
                padding={"11px"}
                onChange={(e) => {
                    setDetails((prev) => ({...prev, email: e.target.value}));
                }}
                value={details.email}
                labelShrink='10px'
                error={Boolean(details?.error)}
                helperText={details?.error ? details.error : ""}
                width={"100%"}
            />
            <CommonButton
                value={"Verify"}
                width={"100%"}
                padding={"10px"}
                fontWeight={"700"}
                fontSize={"14px"}
                borderRadius={"10px"}
                onClick={handleVerify}
                disabled={loading}
                loading={loading}
            />
            </CustomDiv>

            {/* <CommonGrid2 
                display={"flex"} 
                flexDirection={"column"}
                alignItems={"center"}
                justifyContent={"center"}
                customStyle={{ marginTop: "5rem" }}
            >
                <Typography fontSize={"10px"} color={"#5B738B"} textAlign={"center"}>Powered by TalAiro</Typography>
                <TalairoLogo height="40px" />
            </CommonGrid2> */}
        </CommonGrid2>
        </CustomDiv>
    )}
    <SnackBar snackbarState={snackbarState} handleCloseValidation={handleCloseValidation} />
    </Fragment>
    )
}

export default DynamicIntelligence;